import { useEffect, useRef } from 'react';

import { BRAND_NAME } from '../config/brand';

/**
 * The browser tab's title: the object a page is about, then the brand —
 * `Blood pressure · keepr` — or the brand alone when a page names nothing.
 *
 * The context bar calls this with its own title, so the tab says what the
 * bar says on every route that has one: a collection's name, a card's, an
 * item's title. A page without a bar leaves the tab at the brand.
 *
 * One bar per route is the rule (components/common/ContextBar.js), but the
 * tab never depends on it: every caller holds a place in a stack, the last
 * one mounted that names something names the tab (a bar with no title of its
 * own never hides one that has), and an unmount hands the tab back to the
 * ones still mounted — never to whatever title happened to be there before.
 *
 * A link preview never sees this: a chat app reads the HTML Firebase serves,
 * not the page after it runs (scripts/build-link-previews.mjs).
 */

// A title that already ends with the brand ("Connect Claude to keepr") is
// not given it twice.
const ENDS_WITH_BRAND = new RegExp(`\\b${BRAND_NAME}$`, 'i');

export function formatDocumentTitle(name) {
	const text = typeof name === 'string' ? name.replace(/\s+/g, ' ').trim() : '';
	if (!text) return BRAND_NAME;
	return ENDS_WITH_BRAND.test(text) ? text : `${text} · ${BRAND_NAME}`;
}

const entries = [];

function applyTop() {
	const top = [...entries].reverse().find(entry => entry.name.trim());
	document.title = formatDocumentTitle(top?.name);
}

export default function useDocumentTitle(name) {
	const entryRef = useRef(null);
	const text = typeof name === 'string' ? name : '';

	useEffect(() => {
		const entry = { name: '' };
		entryRef.current = entry;
		entries.push(entry);
		return () => {
			const index = entries.indexOf(entry);
			if (index !== -1) entries.splice(index, 1);
			applyTop();
		};
	}, []);

	useEffect(() => {
		entryRef.current.name = text;
		applyTop();
	}, [text]);
}
